import { useMemo } from 'react';

import { useEventInfo } from '@shared/utils/eventInfo';
import { calculateStayDates } from '@shared/utils/dateHelpers';
import { useHotelRoutePaths } from '@shared/utils/accommodations/hotelRoutes';
import type { UtmParameters } from '@shared/core/router.helper/RoutesContext';
import { buildCarSearchParams } from '@shared/utils/accommodations/carSearchParams';
import { useEventCarDefaultLocation } from '@shared/utils/accommodations/useEventCarDefaultLocation';

type StayDates = Readonly<{ checkIn: string; checkOut: string }>;

/**
 * Builds the `/:eventHandle/car/search` URL prefilled from event info with the given UTM parameters.
 * Pass `stayDates` to match a hotel stay the page already picked (e.g. the host's travel settings dates).
 */
export const useEventCarSearchUrl = (utm: UtmParameters, stayDates?: StayDates): string | undefined => {
  const { eventHandle, eventInfo } = useEventInfo();

  const hotelRoutePaths = useHotelRoutePaths();
  const finalizedDateString = eventInfo?.finalizedDate?.dateString;
  const { location, loading } = useEventCarDefaultLocation();

  return useMemo(() => {
    // Waits for the default location, so the link never flips from the venue to the airport under the guest.
    if (!eventHandle || loading) {
      return undefined;
    }

    // Otherwise ±1 day around the event date, defaulting to 1 month out if date is missing or past.
    const eventStayDates = calculateStayDates(finalizedDateString);
    const checkIn = stayDates?.checkIn ?? eventStayDates.checkIn.dateString;
    const checkOut = stayDates?.checkOut ?? eventStayDates.checkOut.dateString;

    return hotelRoutePaths.carSearch.buildRelativeUrl(eventHandle, buildCarSearchParams({ checkIn, checkOut, destination: location, utm }));
  }, [eventHandle, loading, finalizedDateString, location, hotelRoutePaths, utm, stayDates]);
};
